import React, { useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { event } from '../analytics';

const SUBMIT_CONTACT_SALES = gql`
  mutation SubmitContactSales($input: SubmitContactSalesInput!) {
    submitContactSales(input: $input) {
      success
      message
    }
  }
`;

const ContactSales: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [notes, setNotes] = useState('');
  const [submitContactSales, { loading, data, error }] = useMutation(SUBMIT_CONTACT_SALES);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await submitContactSales({
      variables: {
        input: { name, email, companyName, notes },
      },
    });
    if (result.data?.submitContactSales?.success) {
      event('sales_outreach_submit', { event_category: 'sales', event_action: 'outreach_submit' });
    }
  };

  const success = data?.submitContactSales?.success;
  const message = data?.submitContactSales?.message;

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e3a5f', mb: 2 }}>
        Contact Sales
      </Typography>

      <Box
        sx={{
          bgcolor: '#f0fdfa',
          border: '1px solid #99f6e4',
          borderRadius: 2,
          p: 2.5,
          mb: 3,
        }}
      >
        <Typography variant="body1" sx={{ color: '#134e4a' }}>
          Verit Compliance is interested in early customers and partners who are open to providing
          product feedback and usage insight in exchange for a heavily discounted price. If that
          sounds like you, we’d love to hear from you.
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}
      {data && !success && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            margin="normal"
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Your email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            margin="normal"
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Your company name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            margin="normal"
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Additional notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={4}
            margin="normal"
            variant="outlined"
            placeholder="Tell us about your use case, timeline, or any questions..."
          />
          <Box sx={{ mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              disabled={loading}
              sx={{
                background: 'linear-gradient(135deg, #1e3a5f 0%, #134e4a 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #2d4a6f 0%, #1e5f54 100%)' },
              }}
            >
              {loading ? 'Sending...' : 'Send to Sales'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ContactSales;
